import type { ReactNode } from 'react';
import { AudioLines } from 'lucide-react';
import { Link } from '@tanstack/react-router';

/**
 * Scoped keyframes for the auth screens, mirroring what LandingStyles does for
 * the marketing page: the source design ships these as a bare <style> block, so
 * a small per-feature stylesheet reproduces that rather than growing index.css
 * with keyframes only these pages use. `mn-` prefixed so they cannot collide
 * with tailwindcss-animate's names.
 */
const authCss = `
@keyframes mn-wave { 0%, 100% { transform: scaleY(0.2); } 50% { transform: scaleY(1); } }
@keyframes mn-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.15; } }
@keyframes mn-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
`;

interface Props {
  left: ReactNode;
  right: ReactNode;
}

/**
 * The two-column auth shell — editorial copy left, the elevated form card right.
 *
 * No media query anywhere: `repeat(auto-fit, minmax(340px, 1fr))` is the source
 * design's own responsive idiom, and it is what collapses the two columns under
 * roughly 700px of combined width. A breakpoint here would key off the viewport
 * rather than off whether two 340px columns actually fit.
 *
 * The Laravel original also carries a flash-message host, an impersonation
 * banner and a system-administrator link. None are ported: this app has no
 * flash-prop mechanism (mutations toast directly), no impersonation, and its
 * admin surface is sqladmin at /admin — which is not worth advertising from a
 * public page.
 *
 * The brand mark links home, which is what the previous login page's explicit
 * "Back to home" link did.
 */
export function AuthSplitLayout({ left, right }: Props) {
  return (
    <>
      <AuthStyles />

      <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
        <div className="relative z-[1] mx-auto max-w-[1280px] px-[clamp(20px,5vw,56px)] pt-[clamp(20px,4vh,40px)]">
          <Link to="/" className="inline-flex items-center gap-[10px] text-foreground transition-colors duration-200">
            <span className="grid size-[30px] place-items-center rounded-[9px] bg-primary text-primary-foreground">
              <AudioLines className="size-[17px]" />
            </span>
            <span className="text-[16px] font-semibold">MohairNotes</span>
          </Link>
        </div>

        <div
          style={{ animation: 'mn-rise .5s ease both' }}
          className="relative z-[1] mx-auto grid max-w-[1280px] grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-center gap-[clamp(20px,4vw,56px)] px-[clamp(20px,5vw,56px)] py-[clamp(24px,5vh,60px)]">
          <div className="max-w-[480px]">{left}</div>
          <div className="surface-elevated w-full max-w-[460px] justify-self-end rounded-[22px] p-[clamp(26px,4vw,44px)]">
            {right}
          </div>
        </div>
      </div>
    </>
  );
}

function AuthStyles() {
  return <style>{authCss}</style>;
}
